import { sentryMask } from 'sentryReplayPrivacy';
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormHelperText,
  Select,
  MenuItem,
} from '@mui/material';
import MobilePageContent from 'app/pages/mobile/mobile-layout/MobilePageContent';
import { BACKGROUND_SIZES, BACKGROUND_SIZES_v2 } from 'app/pages/mobile/utils/constants';
import { useTranslation } from 'react-i18next';
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  receiptItemsRadioOptions,
  transactionMethodSelectOptions,
} from 'app/pages/overview/tabs/reimbursements/utils';
import type { AgaveLocation, ReceiptLineItems } from 'API';
import InputMask from 'react-text-mask';
import { createNumberMask } from 'text-mask-addons';
import MobileReceiptPreview from 'app/pages/mobile/pages/mobile-transactions/MobileReceiptPreview';
import TransactionField from 'app/pages/mobile/pages/mobile-transactions/TransactionField';
import { useAccountType } from 'app/hooks/useAccountType';
import { useAppSelector, useAppDispatch } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { useSnackbar } from 'notistack';
import { agaveService } from 'app/services/agave.service';
import { captureException } from '@sentry/react';
import { formatCurrency, parseCurrencyToCents } from 'app/shared-components/util/Currency';
import { sanitizeText } from 'util/sanitizeText';
import { format } from 'date-fns';
import MobileLoadingOverlay from 'app/pages/mobile/components/MobileLoadingOverlay';
import { useCodingSystemConfig } from 'app/hooks/transactions/useCodingSystemConfig';
import { clearAllFlexCodingSearches } from 'app/pages/store/flexCodingSlice';
import { useCodingDisplay } from 'app/hooks/useCodingDisplay';
import { MobileCodingFieldList } from 'app/pages/mobile/components/MobileCodingFieldList';
import MobileCodingMenu from 'app/pages/mobile/pages/mobile-transactions/MobileCodingMenu';
import {
  type CodingOptionKey,
  type WatchedCodingValues,
  type CurrentCodingValues,
} from 'app/pages/mobile/types/mobileCodingTypes';
import { useIsEnabled } from 'app/shared-components/feature-flags/FeatureFlagProvider';
import { type CodingType, type CodingColumnId } from 'app/util/columnVisibility';
import { REIMBURSEMENT_CODING_TYPE_COLUMNS } from 'app/pages/overview/tabs/reimbursements/codingTypes';
import {
  formatMileageAmountDollars,
  isAllowedMileageInput,
  isValidMileage,
} from 'app/pages/overview/tabs/reimbursements/mileage';
import {
  useReimbursementForm,
  type ReimbursementFormValues,
  DEFAULT_VALUES,
  mapFormToTransactionShape,
} from './useReimbursementForm';

// ─── Component ────────────────────────────────────────────────────────────────

interface MobileAddReimbursementProps {
  open: boolean;
  onClose: () => void;
  onNext: () => void;
}

type Step = 'step1' | 'step2' | 'coding' | 'review';

interface CurrencyInputProps {
  [key: string]: unknown;
}

const CurrencyInput = (props: CurrencyInputProps) => {
  const currencyMask = createNumberMask({
    prefix: '$',
    allowDecimal: true,
    thousandsSeparatorSymbol: ',',
    decimalSymbol: '.',
  });
  return <InputMask mask={currencyMask} {...props} />;
};

const MobileAddReimbursement = ({ open, onClose, onNext }: MobileAddReimbursementProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { selectedPaidolId, selectedPaidol } = useAppSelector(selectUserCompanies);
  const { hasErpCodingUI, isConstructionType } = useAccountType();
  const useAgave = hasErpCodingUI && isConstructionType;

  const {
    visibleColumnIds,
    isVisible,
    getLabel,
    getMissingRequired,
    isAgaveClient: isAgaveCoding,
    isFlexAccount,
  } = useCodingSystemConfig();

  // SEE-1148 — the GL/Project/Equipment type selector is flag-gated (matches the
  // web reimbursement sheet). Flag OFF → no selector, coding list renders flat as
  // before. Flag ON (Vista) → selector + per-type field filtering, so equipment
  // shows only under the Equipment type (and is now selectable).
  const { 'equipment.enableEquipmentCoding': isEquipmentCodingEnabled } = useIsEnabled([
    { featureName: 'equipment', flagName: 'enableEquipmentCoding' },
  ]);
  const showTypeSelector = useAgave && isEquipmentCodingEnabled;
  const [selectedCodingType, setSelectedCodingType] = useState<CodingType>('gl');

  // GL Code + Vendor plus the selected type's group, intersected with the admin's
  // configured columns (order preserved). undefined => flat list (selector off).
  const codingColumnIds = useMemo<CodingColumnId[] | undefined>(() => {
    if (!showTypeSelector) return undefined;
    return REIMBURSEMENT_CODING_TYPE_COLUMNS[selectedCodingType].filter((id) =>
      visibleColumnIds.includes(id)
    );
  }, [showTypeSelector, selectedCodingType, visibleColumnIds]);
  const { getDisplayName } = useCodingDisplay();

  const showCodingStep = visibleColumnIds.length > 0;
  const totalSteps = showCodingStep ? 3 : 2;

  const mileageRate = selectedPaidol?.reimbursementSettings?.mileageRate ?? null;

  // ── Form ──────────────────────────────────────────────────────────────────

  const { form, isSubmitting, onSubmit } = useReimbursementForm({ onClose, onNext });
  const {
    control,
    watch,
    setValue,
    trigger,
    reset,
    formState: { errors },
  } = form;

  // Watch all fields so the review step and option-menu displays stay reactive.
  const formValues = watch();
  const {
    amount,
    receiptLineItems,
    transactionMethod,
    merchantName,
    transactionDate,
    milesDriven,
    purposeOfTrip,
    notes,
    glCode,
    project,
    subJob,
    costCode,
    costType,
    vendor,
    location,
    department,
    subaccount,
    employee,
    billableItem,
    equipment,
    emCostCode,
    emCostType,
  } = formValues;

  // Mileage mode: org has a rate and the user entered a positive mileage. The amount
  // is then derived from miles × rate (and locked), and merchant becomes optional.
  const isMileageMode =
    mileageRate != null && !!milesDriven && isValidMileage(milesDriven) && Number(milesDriven) > 0;
  const showPurposeHint = isMileageMode && !purposeOfTrip;

  const codingWatchedValues: WatchedCodingValues = useMemo(
    () => ({
      glCode,
      project,
      subJob,
      costCode,
      costType,
      vendor,
      location,
      department,
      subaccount,
      employee,
      billableItem,
      equipment,
      emCostCode,
      emCostType,
    }),
    [
      glCode,
      project,
      subJob,
      costCode,
      costType,
      vendor,
      location,
      department,
      subaccount,
      employee,
      billableItem,
      equipment,
      emCostCode,
      emCostType,
    ]
  );

  const codingCurrentValues: CurrentCodingValues = useMemo(
    () => ({
      glCode,
      projectCode: project,
      subJob,
      costCode,
      costType,
      vendor,
      location,
      department,
      subaccount,
      employee,
      billableItem,
      equipment,
      emCostCode,
      emCostType,
    }),
    [
      glCode,
      project,
      subJob,
      costCode,
      costType,
      vendor,
      location,
      department,
      subaccount,
      employee,
      billableItem,
      equipment,
      emCostCode,
      emCostType,
    ]
  );

  // ── UI-only state ─────────────────────────────────────────────────────────

  const [currentStep, setCurrentStep] = useState<Step>('step1');
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState<string | null>(null);
  const [currentCodingField, setCurrentCodingField] = useState<CodingOptionKey | null>(null);
  const [isCodingMenuOpen, setIsCodingMenuOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) {
      reset(DEFAULT_VALUES);
      setCurrentStep('step1');
      setCurrentCodingField(null);
      setIsCodingMenuOpen(false);
      setSelectedCodingType('gl');
      dispatch(clearAllFlexCodingSearches());
      if (receiptPreviewUrl) {
        URL.revokeObjectURL(receiptPreviewUrl);
        setReceiptPreviewUrl(null);
      }
    }
  }, [open, receiptPreviewUrl, reset, dispatch]);

  useEffect(() => {
    return () => {
      if (receiptPreviewUrl) URL.revokeObjectURL(receiptPreviewUrl);
    };
  }, [receiptPreviewUrl]);

  useEffect(() => {
    contentRef.current?.parentElement?.scrollTo(0, 0);
  }, [currentStep]);

  // In mileage mode the amount is derived from miles × rate and locked. When the
  // org has a rate but miles is cleared/invalid, reset the amount to blank so a
  // stale derived value isn't left behind (the field then becomes editable again).
  useEffect(() => {
    if (mileageRate == null) return;
    if (isMileageMode) {
      setValue('amount', formatMileageAmountDollars(Number(milesDriven), mileageRate), {
        shouldValidate: true,
      });
    } else {
      setValue('amount', '', { shouldValidate: false });
    }
  }, [isMileageMode, mileageRate, milesDriven, setValue]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const reviewAmountDisplay = useMemo(() => {
    try {
      return amount ? formatCurrency({ value: parseCurrencyToCents(amount) }) : '--';
    } catch {
      return '--';
    }
  }, [amount]);

  // ── Navigation ────────────────────────────────────────────────────────────

  const handleNext = useCallback(async () => {
    if (currentStep === 'step1') {
      const valid = await trigger([
        'amount',
        'merchantName',
        'transactionDate',
        'receiptLineItems',
        'transactionMethod',
        'milesDriven',
        'purposeOfTrip',
      ]);
      if (valid) setCurrentStep(showCodingStep ? 'coding' : 'step2');
    } else if (currentStep === 'coding') {
      const missing = getMissingRequired(mapFormToTransactionShape(formValues, isAgaveCoding, isFlexAccount));
      if (missing.length > 0) {
        enqueueSnackbar(`Missing required fields: ${missing.join(', ')}`, { variant: 'warning' });
        return;
      }
      setCurrentStep('step2');
    } else if (currentStep === 'step2') {
      const valid = await trigger(['receiptFile']);
      if (valid) setCurrentStep('review');
    }
  }, [
    currentStep,
    showCodingStep,
    trigger,
    getMissingRequired,
    formValues,
    enqueueSnackbar,
    isAgaveCoding,
    isFlexAccount,
  ]);

  const handleBack = useCallback(() => {
    if (currentStep === 'review') setCurrentStep('step2');
    else if (currentStep === 'step2') setCurrentStep(showCodingStep ? 'coding' : 'step1');
    else if (currentStep === 'coding') setCurrentStep('step1');
  }, [currentStep, showCodingStep]);

  // ── File ──────────────────────────────────────────────────────────────────

  const handleFileSelect = useCallback(
    (file: File) => {
      setValue('receiptFile', file, { shouldValidate: false });
      const url = URL.createObjectURL(file);
      setReceiptPreviewUrl(url);
    },
    [setValue]
  );

  // ── Coding ────────────────────────────────────────────────────────────────

  const handleOpenCodingOptions = useCallback((field: CodingOptionKey) => {
    setCurrentCodingField(field);
    setIsCodingMenuOpen(true);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleCodingSelect = useCallback(
    (option: any) => {
      if (!currentCodingField) return;
      setValue(currentCodingField as keyof ReimbursementFormValues, option);

      if (currentCodingField === 'project') {
        setValue('subJob', null);
        setValue('costCode', null);
        setValue('costType', null);
      }
      if (currentCodingField === 'costCode') {
        setValue('costType', null);
      }
      // SEE-1148 — changing equipment invalidates the EM cost code/type, which
      // are scoped to the equipment's emGroup (mirrors the web equipment change).
      if (currentCodingField === 'equipment') {
        setValue('emCostCode', null);
        setValue('emCostType', null);
      }

      // SEE-944 — when location changes and the toggle is on, clear the
      // project so the dropdown refilters cleanly to the new location.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const erpSetting = (selectedPaidol as any)?.erpDepartmentLocationSetting;
      const see944On = Boolean(
        erpSetting?.isEnabled &&
          erpSetting?.locationEnabled &&
          erpSetting?.filterProjectsByEmployeeLocationEnabled
      );
      if (see944On && currentCodingField === 'location') {
        setValue('project', null);
      }
      // SEE-944 — when employee changes and the toggle is on, auto-fill the
      // location if the employee has exactly one mapped location. Multiple →
      // user picks from the (narrowed) location dropdown.
      if (see944On && currentCodingField === 'employee' && option?.id && selectedPaidolId) {
        const employeeId = option.id;
        const paidolId = selectedPaidolId;
        (async () => {
          try {
            const result = await agaveService.v2SearchAgaveLocations({
              input: { filter: { paidolId, employeeId }, limit: 2 },
            });
            const items = (result?.items ?? []) as AgaveLocation[];
            if (items.length === 1) {
              setValue('location', items[0]);
              // Clear any stale project under the previous location.
              setValue('project', null);
            }
          } catch (e) {
            captureException(e);
          }
        })();
      }

      setIsCodingMenuOpen(false);
      setCurrentCodingField(null);
    },
    [currentCodingField, setValue, selectedPaidol, selectedPaidolId]
  );

  const handleClearCodingOption = useCallback(
    (field: CodingOptionKey) => {
      setValue(field as keyof ReimbursementFormValues, null);
    },
    [setValue]
  );

  // SEE-1148 — switch coding type: sync the persisted codingType and clear the
  // off-type group so a hidden stale code can't be submitted. GL Code + Vendor
  // are common to every type and are never cleared.
  const handleTypeChange = useCallback(
    (next: CodingType) => {
      setSelectedCodingType(next);
      setValue('codingType', next);
      if (next !== 'project') {
        setValue('project', null);
        setValue('subJob', null);
        setValue('costCode', null);
        setValue('costType', null);
      }
      if (next !== 'equipment') {
        setValue('equipment', null);
        setValue('emCostCode', null);
        setValue('emCostType', null);
      }
    },
    [setValue]
  );

  // ── Display helpers ───────────────────────────────────────────────────────

  const getReceiptLineItemsDisplay = () => {
    const option = receiptItemsRadioOptions.find((opt) => opt.value === receiptLineItems);
    return option ? t(option.label) : '';
  };

  const getTransactionMethodDisplay = () => {
    if (!transactionMethod) return '--';
    const option = transactionMethodSelectOptions.find((opt) => opt.value === transactionMethod);
    return option ? t(option.label) : '--';
  };

  // ── Review styles ─────────────────────────────────────────────────────────

  const reviewSectionTitleStyle = {
    color: 'common.white',
    fontWeight: 'bold',
    mb: 1,
  };

  // Frosted "liquid glass" card: whiter translucent fill + blur, a bright top
  // edge highlight, and a deep drop shadow along the edges.
  const reviewContainerStyle = {
    bgcolor: 'rgba(255, 255, 255, 0.12)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(255, 255, 255, 0.25)',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.3), 0 10px 32px rgba(0, 0, 0, 0.4)',
    borderRadius: 2,
    p: 2,
    display: 'flex',
    flexDirection: 'column',
  };

  // Hairline divider between fields (skipped on the last field of a card).
  const reviewFieldStyle = {
    '&:not(:last-child)': {
      borderBottom: '1px solid rgba(255, 255, 255, 0.18)',
      pb: 1.5,
      mb: 1.5,
    },
  };

  // ── Shared sub-components ─────────────────────────────────────────────────

  const StepHeader = ({
    title,
    currentStepNum,
    stepTotalSteps,
    filledSegments,
  }: {
    title: string;
    currentStepNum: number;
    stepTotalSteps: number;
    filledSegments: number;
  }) => (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('mobileTransactionEdit.step', 'Step')} {currentStepNum}/{stepTotalSteps}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: filledSegments > 0 ? 0.5 : 0, mt: 1 }}>
        {filledSegments > 0 && (
          <Box sx={{ flex: filledSegments, height: 4, borderRadius: 2, bgcolor: 'primary.main' }} />
        )}
        <Box
          sx={{
            flex: stepTotalSteps - filledSegments,
            height: 4,
            borderRadius: 2,
            bgcolor: 'primary.lighter',
          }}
        />
      </Box>
    </Box>
  );

  const getNextButtonLabel = () => {
    if (currentStep === 'review') return t('reimbursementPage.buttons.submit');
    // Documentation is the last step before review.
    if (currentStep === 'step2') return t('mobileTransactionEdit.review', 'Review');
    return t('mobileTransactionEdit.next');
  };

  const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: 2 } };
  const fieldLabelSx = {
    mb: 1,
    fontSize: 14,
    fontWeight: 700,
    lineHeight: '20px',
    letterSpacing: '0.25px',
    color: 'common.black',
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (!open) return null;

  return (
    <>
      <MobileLoadingOverlay isLoading={isSubmitting} invert={currentStep === 'review'} />
      <MobilePageContent
        title={t('reimbursementPage.submitForm.addReimbursement')}
        transparentHeight={currentStep === 'review' ? BACKGROUND_SIZES.FULL : BACKGROUND_SIZES_v2.SHOW_HEADER}
        onBack={currentStep === 'review' ? undefined : currentStep === 'step1' ? onClose : handleBack}
      >
        <Box
          ref={contentRef}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            minHeight: '100%',
          }}
        >
          <Box sx={{ flex: 1 }}>
            {/* ── Step 1: Details ── */}
            {currentStep === 'step1' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <StepHeader
                  title={t('reimbursementPage.submitForm.title')}
                  currentStepNum={1}
                  stepTotalSteps={totalSteps}
                  filledSegments={1}
                />

                {/* Amount */}
                <Box>
                  <Typography sx={fieldLabelSx}>{t('reimbursementPage.submitForm.amountLabel')}*</Typography>
                  <Controller
                    name="amount"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="$ 0.00"
                        disabled={isMileageMode}
                        InputProps={{ inputComponent: CurrencyInput }}
                        variant="outlined"
                        size="small"
                        error={!!fieldState.error}
                        helperText={
                          isMileageMode
                            ? t('reimbursementPage.submitForm.calculatedFromMileage')
                            : fieldState.error?.message
                        }
                        sx={{
                          ...inputSx,
                          ...(isMileageMode && {
                            '& .MuiOutlinedInput-root': { backgroundColor: 'neutral90.main' },
                            '& .MuiFormHelperText-root': { ml: 0, color: 'neutral50.main' },
                          }),
                        }}
                      />
                    )}
                  />
                </Box>

                {/* This documentation reflects */}
                <Box>
                  <Typography sx={fieldLabelSx}>
                    {t('reimbursementPage.submitForm.receiptLineItemsLabel')}
                  </Typography>
                  <Controller
                    name="receiptLineItems"
                    control={control}
                    render={({ field, fieldState }) => (
                      <>
                        <RadioGroup
                          value={field.value ?? ''}
                          onChange={(e) => field.onChange(e.target.value as ReceiptLineItems)}
                        >
                          {receiptItemsRadioOptions.map((option) => (
                            <FormControlLabel
                              key={option.value}
                              value={option.value}
                              control={<Radio size="small" />}
                              label={t(option.label)}
                            />
                          ))}
                        </RadioGroup>
                        {fieldState.error && (
                          <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                            {fieldState.error.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Box>

                {/* Payment method */}
                <Box>
                  <Typography sx={fieldLabelSx}>
                    {t('reimbursementPage.submitForm.transactionMethodLabel')}*
                  </Typography>
                  <Controller
                    name="transactionMethod"
                    control={control}
                    render={({ field, fieldState }) => (
                      <FormControl fullWidth size="small" error={!!fieldState.error}>
                        <Select
                          {...field}
                          value={field.value ?? ''}
                          displayEmpty
                          renderValue={(value) =>
                            value ? (
                              getTransactionMethodDisplay()
                            ) : (
                              <Typography component="span" sx={{ color: 'text.secondary' }}>
                                {t('reimbursementPage.submitForm.selectPlaceholder')}
                              </Typography>
                            )
                          }
                          MenuProps={{ PaperProps: { sx: { backgroundColor: 'neutral90.main' } } }}
                          sx={inputSx}
                        >
                          {transactionMethodSelectOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {t(option.label)}
                            </MenuItem>
                          ))}
                        </Select>
                        {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                      </FormControl>
                    )}
                  />
                </Box>

                {/* Merchant */}
                <Box>
                  <Typography sx={fieldLabelSx}>
                    {t('reimbursementPage.submitForm.merchantLabelMobile')}
                    {!isMileageMode && '*'}
                  </Typography>
                  <Controller
                    name="merchantName"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ''}
                        fullWidth
                        placeholder={t('reimbursementPage.submitForm.merchantPlaceholder')}
                        variant="outlined"
                        size="small"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        sx={inputSx}
                        onBlur={(e) => field.onChange(sanitizeText(e.target.value))}
                      />
                    )}
                  />
                </Box>

                {/* Transaction date */}
                <Box>
                  <Typography sx={fieldLabelSx}>
                    {t('reimbursementPage.submitForm.transactionDateLabel')}*
                  </Typography>
                  <Controller
                    name="transactionDate"
                    control={control}
                    render={({ field, fieldState }) => (
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          value={field.value}
                          onChange={field.onChange}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              placeholder: t('reimbursementPage.submitForm.datePlaceholder'),
                              variant: 'outlined',
                              size: 'small',
                              error: !!fieldState.error,
                              helperText: fieldState.error?.message,
                              sx: inputSx,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    )}
                  />
                </Box>

                {/* Mileage — only when the org has a per-mile rate configured */}
                {mileageRate != null && (
                  <>
                    {/* Miles driven */}
                    <Box>
                      <Typography sx={fieldLabelSx}>
                        {t('reimbursementPage.submitForm.milesDrivenLabel', 'Miles driven')}
                      </Typography>
                      <Controller
                        name="milesDriven"
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            onChange={(e) => {
                              // Block typing past the second decimal rather than erroring.
                              if (isAllowedMileageInput(e.target.value)) field.onChange(e.target.value);
                            }}
                            fullWidth
                            placeholder="0.00"
                            variant="outlined"
                            size="small"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">miles</InputAdornment>,
                            }}
                            inputProps={{ inputMode: 'decimal' }}
                            sx={inputSx}
                          />
                        )}
                      />
                    </Box>

                    {/* Total reimbursement (derived) */}
                    <Box>
                      <Typography sx={fieldLabelSx}>
                        {t('reimbursementPage.submitForm.totalReimbursementLabel', 'Total reimbursement')}
                      </Typography>
                      <TextField
                        value={`$${formatMileageAmountDollars(Number(milesDriven) || 0, mileageRate)}`}
                        disabled
                        fullWidth
                        variant="outlined"
                        size="small"
                        sx={{
                          ...inputSx,
                          '& .MuiOutlinedInput-root': { backgroundColor: 'neutral90.main' },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', mt: 0.5, ml: 0.5, color: 'neutral50.main' }}
                      >
                        {t('reimbursementPage.submitForm.currentRateLabel', {
                          rate: `$${mileageRate.toFixed(2)}`,
                        })}
                      </Typography>
                    </Box>

                    {/* Purpose of trip */}
                    <Box>
                      {showPurposeHint && (
                        <Typography sx={{ color: 'error.main', fontSize: 12, lineHeight: '16px', mb: 0.5 }}>
                          {t('reimbursementPage.submitForm.purposeRequiredHint')}
                        </Typography>
                      )}
                      <Controller
                        name="purposeOfTrip"
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ''}
                            fullWidth
                            placeholder={t(
                              'reimbursementPage.submitForm.purposeOfTripLabel',
                              'Purpose of the trip'
                            )}
                            variant="outlined"
                            size="small"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            sx={{
                              ...inputSx,
                              ...(showPurposeHint && {
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'error.main' },
                              }),
                            }}
                          />
                        )}
                      />
                    </Box>
                  </>
                )}
              </Box>
            )}

            {/* ── Documentation & Notes (last step before review) ── */}
            {currentStep === 'step2' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <StepHeader
                  title={t('reimbursementPage.submitForm.documentationLabel')}
                  currentStepNum={totalSteps}
                  stepTotalSteps={totalSteps}
                  filledSegments={totalSteps}
                />

                {/* Additional information + receipt upload */}
                <Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'common.black', mb: 1 }}>
                    {t('reimbursementPage.submitForm.additionalInformationLabel')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'neutral50.main', mb: 2 }}>
                    {t('reimbursementPage.submitForm.receiptHelperText')}
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'primary.dark',
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                    }}
                  >
                    <Typography sx={{ fontSize: 12, lineHeight: '16px', color: 'neutralVariant25.main' }}>
                      {t('reimbursementPage.submitForm.uploadReceiptLabel')}
                    </Typography>
                    <MobileReceiptPreview
                      compactDropzone
                      viewOnly={false}
                      onFileSelect={handleFileSelect}
                      scopeId={selectedPaidolId}
                    />
                  </Box>
                  {errors.receiptFile && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1 }}>
                      {errors.receiptFile.message as string}
                    </Typography>
                  )}
                </Box>

                {/* Notes */}
                <Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'common.black', mb: 1 }}>
                    {t('reimbursementPage.submitForm.notesLabel')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('reimbursementPage.submitForm.noteHelperText')}
                  </Typography>
                  <Controller
                    name="notes"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ''}
                        fullWidth
                        multiline
                        rows={6}
                        placeholder="Enter notes here..."
                        // SEE-1834: notes are free text, masked in session replays.
                        inputProps={sentryMask}
                        variant="outlined"
                        size="small"
                        sx={inputSx}
                      />
                    )}
                  />
                </Box>
              </Box>
            )}

            {/* ── Coding (conditional, step 2) ── */}
            {currentStep === 'coding' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <StepHeader
                  title={t('mobileTransactionEdit.coding', 'Code Transaction')}
                  currentStepNum={2}
                  stepTotalSteps={totalSteps}
                  filledSegments={2}
                />
                {showTypeSelector && (
                  <FormControl fullWidth>
                    <Select
                      value={selectedCodingType}
                      onChange={(e) => handleTypeChange(e.target.value as CodingType)}
                    >
                      {(['gl', 'project', 'equipment'] as CodingType[]).map((type) => (
                        <MenuItem key={type} value={type}>
                          {t(
                            `transactionSidesheet.${type}`,
                            type === 'gl' ? 'GL' : type === 'project' ? 'Project' : 'Equipment'
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <MobileCodingFieldList
                  mode="editable"
                  isVisible={isVisible}
                  getLabel={getLabel}
                  orderedColumnIds={showTypeSelector ? codingColumnIds : visibleColumnIds}
                  currentValues={codingCurrentValues}
                  watchedValues={codingWatchedValues}
                  onOpenOptions={handleOpenCodingOptions}
                  onClearOption={handleClearCodingOption}
                  disabledOptionKeys={
                    useAgave && Boolean(selectedPaidol?.requireProjectBeforeCostCode?.isEnabled) && !project
                      ? ['costCode', 'costType']
                      : undefined
                  }
                />
              </Box>
            )}

            {/* ── Review ── */}
            {currentStep === 'review' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Transaction info */}
                <Box>
                  <Typography variant="subtitle1" sx={reviewSectionTitleStyle}>
                    {t('reimbursementPage.sheet.transactionInfo')}
                  </Typography>
                  <Box sx={reviewContainerStyle}>
                    <TransactionField
                      label={t('reimbursementPage.sheet.amount')}
                      value={reviewAmountDisplay}
                      sx={reviewFieldStyle}
                      invertColor
                    />
                    <TransactionField
                      label={t('reimbursementPage.sheet.thisReflects')}
                      value={getReceiptLineItemsDisplay()}
                      sx={reviewFieldStyle}
                      invertColor
                    />
                    <TransactionField
                      label={t('reimbursementPage.sheet.paymentMethod')}
                      value={getTransactionMethodDisplay()}
                      sx={reviewFieldStyle}
                      invertColor
                    />
                    <TransactionField
                      label={t('reimbursementPage.sheet.merchant')}
                      value={merchantName || '--'}
                      sx={reviewFieldStyle}
                      invertColor
                    />
                    <TransactionField
                      label={t('reimbursementPage.sheet.transactionDate')}
                      value={transactionDate ? format(transactionDate, 'MM/dd/yyyy') : '--'}
                      sx={reviewFieldStyle}
                      invertColor
                    />
                    {isMileageMode && (
                      <>
                        <TransactionField
                          label={t('reimbursementPage.sheet.milesDriven')}
                          value={milesDriven || '--'}
                          sx={reviewFieldStyle}
                          invertColor
                        />
                        <TransactionField
                          label={t('reimbursementPage.sheet.purposeOfTrip')}
                          value={purposeOfTrip || '--'}
                          sx={reviewFieldStyle}
                          invertColor
                        />
                      </>
                    )}
                    <TransactionField
                      label={t('reimbursementPage.sheet.notes')}
                      value={<span {...sentryMask}>{notes || '--'}</span>}
                      sx={reviewFieldStyle}
                      invertColor
                    />
                  </Box>
                </Box>

                {/* Project codes */}
                {showCodingStep && (
                  <Box>
                    <Typography variant="subtitle1" sx={reviewSectionTitleStyle}>
                      {t('reimbursementPage.submitForm.projectCodesLabel')}
                    </Typography>
                    <Box sx={reviewContainerStyle}>
                      <MobileCodingFieldList
                        mode="readonly"
                        isVisible={isVisible}
                        getLabel={getLabel}
                        orderedColumnIds={visibleColumnIds}
                        rawWatchedValues={codingWatchedValues}
                        getDisplayName={getDisplayName as (value: unknown) => string}
                        emptyValue="--"
                        fieldSx={reviewFieldStyle}
                        invertColor
                      />
                    </Box>
                  </Box>
                )}

                {/* Documentation */}
                <Box>
                  <Typography variant="subtitle1" sx={reviewSectionTitleStyle}>
                    {t('reimbursementPage.submitForm.documentationLabel')}
                  </Typography>
                  <Box sx={reviewContainerStyle}>
                    {receiptPreviewUrl ? (
                      <MobileReceiptPreview
                        viewOnly={true}
                        receiptUrl={receiptPreviewUrl}
                        scopeId={selectedPaidolId}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        --
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          {/* Navigation Footer */}
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'primary.lighter',
              borderRadius: 10,
              py: 1,
              px: 1.5,
              mt: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              maxWidth: 400,
              alignSelf: 'center',
              width: '100%',
              zIndex: 10,
            }}
          >
            <Typography
              onClick={onClose}
              sx={{ color: 'primary.main', fontWeight: 500, cursor: 'pointer', px: 2, py: 1 }}
            >
              {t('mobileTransactionEdit.cancel')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {currentStep !== 'step1' && (
                <Button
                  onClick={handleBack}
                  sx={{ borderRadius: 10, px: 3, borderColor: 'primary.main', color: 'primary.main' }}
                  variant="outlined"
                >
                  {t('mobileTransactionEdit.back')}
                </Button>
              )}
              <Button
                onClick={currentStep === 'review' ? onSubmit : handleNext}
                sx={{
                  borderRadius: 10,
                  px: 3,
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)',
                  },
                }}
                variant="contained"
                disabled={isSubmitting}
              >
                {getNextButtonLabel()}
              </Button>
            </Box>
          </Box>
        </Box>
      </MobilePageContent>

      {/* Coding Menu */}
      <MobileCodingMenu
        open={isCodingMenuOpen}
        onClose={() => {
          setIsCodingMenuOpen(false);
          setCurrentCodingField(null);
        }}
        codingType={currentCodingField}
        onSelect={handleCodingSelect}
        getLabel={getLabel}
        selectedGlCode={glCode}
        selectedProject={project}
        selectedCostCode={costCode}
        selectedSubJob={subJob}
        selectedLocation={location}
        selectedEmployee={employee}
        selectedEquipment={equipment}
        selectedEmCostCode={emCostCode}
      />
    </>
  );
};

export default MobileAddReimbursement;
